import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { Contest } from '../../models/types';
import { CardRow } from '../ui/SurfaceCard';
import { StatusPill, PillTone } from '../ui/StatusPill';
import { formatShortDate } from './contestFormat';

export type ContestListStatusLabel = 'Active' | 'Upcoming' | 'Ended';
export type ContestListStatus = { label: ContestListStatusLabel; tone: PillTone; rank: number };

export const getContestListStatus = (contest: Contest, now: Date): ContestListStatus => {
  if (new Date(contest.endAt) <= now) return { label: 'Ended', tone: 'muted', rank: 2 };
  if (new Date(contest.startAt) <= now) return { label: 'Active', tone: 'success', rank: 0 };
  return { label: 'Upcoming', tone: 'accent', rank: 1 };
};

const participantsText = (c: Contest, t: TFunction) => {
  const people = t('contests:listRow.participants', { count: c.numberOfParticipants ?? 0 });
  return c.contestType === 'Individual' ? people : `${t('contests:listRow.teams', { count: c.numberOfTeams ?? 0 })} · ${people}`;
};

interface ContestListRowProps {
  contest: Contest;
  status: ContestListStatus;
  // Shown before the dates when the list spans several groups.
  groupName?: string;
}

export const ContestListRow = ({ contest, status, groupName }: ContestListRowProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('contests');
  const meta = [
    groupName,
    `${formatShortDate(contest.startAt)} – ${formatShortDate(contest.endAt)}`,
    t(`contestType.${contest.contestType || 'Team'}`),
    t(`activityType.${contest.activityType || 'Run'}`),
    participantsText(contest, t),
  ].filter(Boolean);

  return (
    <CardRow onClick={() => navigate(`/contests/${contest._id}`)} sx={{ p: '14px 20px', gap: 1.75, flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box component="span" sx={{ fontSize: 14, fontWeight: 500 }}>{contest.name}</Box>
        <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{meta.join(' · ')}</Box>
      </Box>
      <StatusPill label={t(`listStatus.${status.label}`)} tone={status.tone} />
      <ChevronRightIcon sx={{ color: 'var(--muted)' }} />
    </CardRow>
  );
};
